import {ComponentRef, ViewContainerRef} from "@angular/core";
import {Router} from "@angular/router";
import {Observable, timer} from "rxjs";
import scrollIntoView from 'scroll-into-view-if-needed';

import {MixinConstructor} from "./mixin.define";
import {LINK_POPUP_DELAY, LinkInfo, LIST_TYPE, RESULT_LINKPOPUP, SearchParam} from "../defines/client.const";
import {WordHistoryService} from "../services/word/word-history.service";
import {DynamicComponentService} from "../services/dynamic-component/dynamic-component.service";
import {LinkPopupComponent} from "../components/link-popup/link-popup.component";


type MultiLinkMixinType = {
  router: Router,
  wHistorySvc: WordHistoryService,
  dynamicSvc: DynamicComponentService,
};

/**
 * 단일 mixin을 사용하는 경우의 정의.
 */
export const multiLinkMixin = MultiLinkMixinFunction(
  class Parent {
    constructor(
      public router: Router,
      public wHistorySvc: WordHistoryService,
      public dynamicSvc: DynamicComponentService,
    ) {}
  }
);

export function MultiLinkMixinFunction<BaseType extends MixinConstructor<MultiLinkMixinType>>(BaseClass: BaseType) {
  return class extends BaseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    private elRefMultiLinkPopup: ComponentRef<LinkPopupComponent> | null = null;


    /**
     *
     * @param ids - 단어 ID 들 (콤마로 구분)
     * @param container - 링크 팝업창을 열 기준이 되는 요소
     * @param word - 링크에 표시되는 문자열
     * @param popupLeftOffset - 팝업창의 수평 보정값 (popupLeftTarget이 null일 때만 적용, 0이면 미적용)
     * @param popupZIndex - 팝업 컴포넌트 host 요소의 z-index 값 (0이면 미적용)
     * @param popupLeftTarget - 팝업창 article(또는 host)의 왼쪽을 맞출 화면 기준 x 좌표 (null이면 미적용)
     * @constructor
     */
    public OpenMultiLink(ids: string, container: ViewContainerRef, word: string | null = null, popupLeftOffset = 0, popupZIndex = 0, popupLeftTarget: number | null = null): Observable<RESULT_LINKPOPUP> {
      console.log('MultiLinkMixin::OpenMultiLink() : word=', ids);

      return new Observable<RESULT_LINKPOPUP>(observer => {
        try {
          this.elRefMultiLinkPopup = this.dynamicSvc.createComponent(LinkPopupComponent, container);
          const popupEl = this.elRefMultiLinkPopup.location.nativeElement as HTMLElement;
          if (popupZIndex !== 0) {
            popupEl.style.zIndex = `${popupZIndex}`;
          }

          /******************************************************************************************
           * 팝업창이 닫힐 때 발생하는 이벤트 처리.
           */
          this.elRefMultiLinkPopup.instance.close.subscribe((linkInfo: LinkInfo | null) => {
            console.log('MultiLinkMixin::OpenMultiLink() : linkInfo=', linkInfo);

            observer.next(RESULT_LINKPOPUP.POPUP_CLOSE);

            if (linkInfo) {
              const lenWord = linkInfo.wordIds?.length ?? 0;
              const lenDialect = linkInfo.dialectIds?.length ?? 0;
              if (lenWord + lenDialect >= 2) {
                // 단어와 지방어가 2개 이상이면 링크 페이지로 이동.
                const queryParams: SearchParam = {
                  ...(linkInfo.wordIds && {wids: linkInfo.wordIds.join(',')}), // 단어 ID
                  ...(linkInfo.dialectIds && {dids: linkInfo.dialectIds.join(',')}), // 지방어 ID
                };
                this.router.navigate(['/link'], {queryParams}).then();

                observer.next(RESULT_LINKPOPUP.GOTO_DETAIL);
              }
              else if (linkInfo?.wordIds && lenWord === 1) {
                const id = linkInfo.wordIds[0];
                if (this.wHistorySvc.getById(id) === null) {
                  // 없으면 추가.
                  this.wHistorySvc.addHistory(id);
                }
                const queryParams: SearchParam = {
                  id,
                };
                this.router.navigate(['/word', LIST_TYPE.SERVER], {queryParams}).then();

                observer.next(RESULT_LINKPOPUP.GOTO_LINK);
              }
              else if (linkInfo?.dialectIds && lenDialect === 1) {
                const id = linkInfo.dialectIds[0];
                if (this.wHistorySvc.getById(id) === null) {
                  // 없으면 추가.
                  this.wHistorySvc.addHistory(id);
                }
                const queryParams: SearchParam = {
                  id,
                };
                this.router.navigate(['/dialect-list', LIST_TYPE.SERVER], {queryParams}).then();

                observer.next(RESULT_LINKPOPUP.GOTO_LINK);
              }
            }

            observer.complete();
            this.closeMultiLinkPopup();
          });


          /******************************************************************************************
           * 화면에 초기화 된 후 (ngAfterViewInit()) 발생하는 이벤트 처리.
           */
          this.elRefMultiLinkPopup.instance.viewInit.subscribe((nativeEl: HTMLElement) => {

            // 팝업창이 열리는 애니메이션이 끝난 뒤에 스크롤 해야 정확한 위치로 스크롤이 된다.
            timer(LINK_POPUP_DELAY).subscribe(() => {
              console.log('MultiLinkMixin::OpenMultiLink() : viewInit=', nativeEl);

              scrollIntoView(nativeEl, {
                behavior: 'smooth',
                block: 'end',
                scrollMode: 'if-needed',
              });
            });
          });

          /******************************************************************************************
           * 팝업창에 표시할 정보 설정.
           */
          const [wordIds, dialectIds] = ids.split(';').map((item) => item ? item.split(',') : []);

          this.elRefMultiLinkPopup.instance.linkInfo = {
            ...((wordIds && wordIds.length > 0) ? {wordIds} : {}),
            ...((dialectIds && dialectIds.length > 0) ? {dialectIds} : {}),
            ...(word && {word}),
          };
          this.elRefMultiLinkPopup.changeDetectorRef.detectChanges();

          if (popupLeftTarget !== null) {
            const articleEl = popupEl.querySelector('article') as HTMLElement | null;
            const currentLeft = (articleEl ?? popupEl).getBoundingClientRect().left;
            popupEl.style.transform = `translateX(${popupLeftTarget - currentLeft}px)`;
          }
          else if (popupLeftOffset !== 0) {
            popupEl.style.transform = `translateX(${popupLeftOffset}px)`;
          }
        } catch (err) {
          console.error('MultiLinkMixin::OpenMultiLink() : Failed to create popup', err);
          observer.error(err);
        }
      });
    }

    private closeMultiLinkPopup(): void {
      if (this.elRefMultiLinkPopup) {
        this.elRefMultiLinkPopup.destroy();
        this.elRefMultiLinkPopup = null;
      }
    }

  }
}
